import React from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { format } from 'date-fns';
import UserAvatar from 'components/shared/user/UserAvatar';
import StyledWeeklySessionEventContainer from 'common/theme/calendar/event/week/StyledWeeklySessionEventContainer';
import { getSessionLineColor, isPastSession, dimIfPastSx } from 'common/utils/sessionColor';

const fmtTime = (d) => {
  try { return format(new Date(d), 'HH:mm'); } catch { return ''; }
};

/**
 * Card de sesión grupal (Active Plus) en la vista semanal. Mismo layout que las cards uno-a-uno:
 * avatar + nombre del entrenador + categoría en DORADO ("Active Plus") + rango horario, con franja
 * lateral por estado y atenuado si ya pasó.
 */
function GroupSessionWeeklyEvent({ event }) {
  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const { extendedProps } = event;
  const { trainer, sessionTypeName, status } = extendedProps;
  const displayUser = trainer ?? { full_name: 'Active Plus' };
  // Distintivo de membresía + tipo de clase: "Active Plus · Full Body". Si el tipo ya es "active plus"
  // (o no hay tipo) NO se duplica: muestra solo "Active Plus".
  const showType = sessionTypeName && !sessionTypeName.trim().toLowerCase().startsWith('active plus');
  const category = showType ? `Active Plus · ${sessionTypeName}` : 'Active Plus';
  const lineColor = getSessionLineColor(theme, status ?? 'SCHEDULED', event.start);
  const past = isPastSession(event.start, event.end);
  const timeRange = event.start ? `${fmtTime(event.start)}${event.end ? ` – ${fmtTime(event.end)}` : ''}` : '';

  return (
    <StyledWeeklySessionEventContainer sx={{ borderLeft: `4px solid ${lineColor}`, ...dimIfPastSx(past) }}>
      <Box sx={{ flex: 1, minWidth: 0, p: { xs: 0.75, sm: 1 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.25 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
          <UserAvatar size={isExtraSmall ? 18 : 24} textSize={isExtraSmall ? 9 : 12} user={displayUser} />
          <Typography noWrap sx={{ fontWeight: 700, fontSize: isExtraSmall ? '0.7rem' : '0.78rem', color: '#FFFFFF' }}>
            {displayUser.full_name}
          </Typography>
        </Stack>
        <Typography noWrap sx={{ fontSize: isExtraSmall ? '0.62rem' : '0.7rem', color: '#F9BD4E', fontWeight: 600, lineHeight: 1.2 }}>
          {category}
        </Typography>
        {timeRange && (
          <Typography noWrap sx={{ fontSize: isExtraSmall ? '0.6rem' : '0.68rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.2 }}>
            {timeRange}
          </Typography>
        )}
      </Box>
    </StyledWeeklySessionEventContainer>
  );
}

GroupSessionWeeklyEvent.propTypes = {
  event: PropTypes.object.isRequired
};

export default GroupSessionWeeklyEvent;
